import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Server, Database, Brain, Rocket, Globe, BarChart } from "lucide-react";

// --- Helper Components ---

// StarRating Helper
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-500" : "text-gray-300" 
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Verified Badge
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 ml-2 border border-gray-300">
    <svg
      className="w-3 h-3 mr-1 text-indigo-600"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

// --- Tools Section (Advanced Python Specific) ---
const ToolsYouWillMaster = () => {
  const tools = [
    { name: 'Django', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/django/django-plain.svg' },
    { name: 'Flask', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flask/flask-original.svg' },
    { name: 'NumPy', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/numpy/numpy-original.svg' },
    { name: 'Pandas', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/pandas/pandas-original.svg' },
    { name: 'PostgreSQL', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg' },
    { name: 'Docker', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg' },
    { name: 'AWS', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/93/Amazon_Web_Services_Logo.svg/2560px-Amazon_Web_Services_Logo.svg.png' }, // AWS
    { name: 'Postman', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postman/postman-original.svg' },
  ];

  return (
    <section className="py-20 bg-gray-100">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-black font-aligran">
          Advanced <span className="text-indigo-600">Tech Stack</span>
        </h2>
        
        <div className="grid grid-cols-4 md:grid-cols-8 gap-8 mb-8">
          {tools.map((tool, i) => (
            <motion.div
              key={i}
              className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
            >
              <img
                src={tool.logo}
                alt={`${tool.name} Logo`}
                className="w-16 h-16 mb-2 object-contain"
              />
              <p className="text-sm font-semibold text-black mt-2">{tool.name}</p>
            </motion.div>
          ))}
        </div>
        
        <p className="text-gray-600 max-w-2xl mx-auto">
          Move beyond scripts. Master <b>Web Frameworks</b> (Django/Flask), <b>Data Science Libraries</b> (Pandas/NumPy), and <b>Cloud Deployment</b>.
        </p>
      </div>
    </section>
  );
};

const AdvancePython = () => {
  // --- Data ---
  const testimonials = [
    {
      id: 1,
      name: "Vikram Sethi",
      review:
        "I knew Python basics, but this course taught me how to actually build things. The Django E-commerce project was an eye-opener.",
      rating: 5,
      date: "2024-08-22",
      profileImg: "https://randomuser.me/api/portraits/men/33.jpg", 
      source: "Google Reviews",
    },
    {
      id: 2,
      name: "Anjali Mehta",
      review:
        "Data Science modules were fantastic. Pandas and Matplotlib helped me automate my data reporting tasks at work.",
      rating: 5,
      date: "2024-08-15",
      profileImg: "https://randomuser.me/api/portraits/women/41.jpg", 
      source: "LinkedIn",
    },
    {
      id: 3,
      name: "Rahul Verma",
      review:
        "The API development section using Django REST Framework is very detailed. I can now build backends for React apps easily.",
      rating: 4,
      date: "2024-08-08",
      profileImg: "https://randomuser.me/api/portraits/men/50.jpg", 
      source: "Website Feedback",
    },
    {
      id: 4,
      name: "Sneha Roy",
      review:
        "Best advanced course. They cover everything from Virtual Environments to deploying on AWS EC2. Highly recommended.",
      rating: 5,
      date: "2024-07-30",
      profileImg: "https://randomuser.me/api/portraits/women/62.jpg",
      source: "Google Reviews",
    },
    {
      id: 5,
      name: "Amit Kumar",
      review:
        "I got a job as a Python Backend Developer after this. The focus on ORM and Database connectivity is exactly what companies ask for.",
      rating: 4,
      date: "2024-07-25",
      profileImg: "https://randomuser.me/api/portraits/men/12.jpg",
      source: "LinkedIn",
    },
  ];

  const faqContent = [
    {
      title: "What are the prerequisites?",
      answer:
        "You must know Core Python (Variables, Loops, Functions, OOPs). This course starts directly with Advanced concepts and Frameworks.",
    },
    {
      title: "Django vs Flask - What will I learn?",
      answer:
        "You will learn both! Flask for lightweight microservices and Django for full-stack, scalable enterprise applications.",
    },
    {
      title: "Does this cover Data Science?",
      answer:
        "Yes, we cover the essential Data Science stack: NumPy for calculations, Pandas for data analysis, and Matplotlib for visualization.",
    },
    {
      title: "Will I learn API Development?",
      answer:
        "Absolutely. A major part of the course focuses on building RESTful APIs using Django REST Framework (DRF) for mobile and web apps.",
    },
    {
      title: "What projects will I build?",
      answer:
        "You will build 3 major projects: A Data Analytics Dashboard, a Blogging Platform (Flask), and a full-fledged E-commerce Store (Django).",
    },
  ];

  const curriculumContent = [
    {
      title: "Module 1: Advanced Python Concepts",
      description: "Decorators, Generators, Iterators, Context Managers, Multithreading, and Metaclasses.",
    },
    {
      title: "Module 2: Database Connectivity (MySQL/PostgreSQL)",
      description: "Raw SQL queries vs ORM, connecting Python to databases, Transaction management, and CRUD operations.",
    },
    {
      title: "Module 3: Data Science Basics (NumPy & Pandas)",
      description: "Array manipulation, DataFrames, Data Cleaning, and analyzing large datasets efficiently.",
    },
    {
      title: "Module 4: Data Visualization (Matplotlib & Seaborn)",
      description: "Creating graphs, charts, and dashboards to visualize data trends and insights.",
    },
    {
      title: "Module 5: Web Development with Flask",
      description: "Routing, Jinja2 Templates, Form handling, and building lightweight web applications.",
    },
    {
      title: "Module 6: Django Framework (The Powerhouse)",
      description: "MVT Architecture, ORM, Admin Panel, Authentication system, and Project Structure.",
    },
    {
      title: "Module 7: REST API with Django REST Framework",
      description: "Building APIs, Serializers, Authentication (JWT), and testing endpoints with Postman.",
    },
    {
      title: "Module 8: Deployment & Cloud (AWS)",
      description: "Gunicorn, Nginx, Dockerizing apps, and deploying them live on AWS EC2 or Heroku.",
    },
  ];

  // Highlights Data with Advanced Python Specific Images
  const highlights = [
    { 
      title: 'Django Mastery', 
      desc: 'Build scalable web apps.', 
      img: 'https://images.unsplash.com/photo-1610986603166-f78428624e76?q=80&w=2070' // Code/Web
    },
    { 
      title: 'Data Science', 
      desc: 'Analyze data with Pandas.', 
      img: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070' // Graphs/Data
    },
    { 
      title: 'REST APIs', 
      desc: 'Create APIs for Mobile/Web.', 
      img: 'https://images.unsplash.com/photo-1516259762381-22954d7d3ad2?q=80&w=2066' // Server/Code
    },
    { 
      title: 'Cloud Deploy', 
      desc: 'Host apps on AWS/Heroku.', 
      img: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072' // Cloud/Server
    },
    { 
      title: 'Live Projects', 
      desc: 'E-commerce & Dashboards.', 
      img: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2015' // Laptop/Work
    },
    { 
      title: 'Automation', 
      desc: 'Write scripts to save time.', 
      img: 'https://images.unsplash.com/photo-1518432031352-d6fc5c10da5a?q=80&w=1974' // Robot/Tech
    },
    { 
      title: 'Expert Mentors', 
      desc: 'Learn from Senior Devs.', 
      img: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2070' // Team
    },
    { 
      title: 'Certification', 
      desc: 'Advanced Python Certificate.', 
      img: 'https://images.unsplash.com/photo-1589330273594-fade1ee91647?q=80&w=2070' // Certificate
    },
    { 
      title: 'Placement', 
      desc: 'Job support for Backend roles.', 
      img: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=2069' // Handshake
    },
  ];

  // --- Logic for Carousel ---
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setVisibleCount(3);
      else if (window.innerWidth >= 768) setVisibleCount(2);
      else setVisibleCount(1);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(autoScrollInterval);
  }, [testimonials.length]);

  const currentVisibleTestimonials = [];
  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(
      testimonials[(currentTestimonialIndex + i) % testimonials.length]
    );
  }

  // --- Logic for Form ---
  const [formData, setFormData] = useState({ firstName: "", businessEmail: "", phone: "" });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert("Thank you! We will contact you shortly regarding the Advanced Python course.");
    setFormData({ firstName: "", businessEmail: "", phone: "" });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="bg-white text-gray-900 font-sans">
      <Helmet>
        <title>Advanced Python Course | Django, Data Science & AI</title>
        <meta name="description" content="Master Advanced Python. Learn Django, Flask, REST APIs, and Data Science (NumPy/Pandas)." />
      </Helmet>

      {/* --- HERO SECTION --- */}
      <section className="relative overflow-hidden h-[550px] flex items-center bg-black"> 
      
      {/* Background Image + Gradient */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1518932945647-7a1c969f8be2?q=80&w=2070&auto=format&fit=crop"
          alt="Advanced Python"
          className="w-full h-full object-cover opacity-30"
        />
        {/* Indigo Gradient for Advanced Theme */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-indigo-900/40" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center w-full pt-20">

        {/* Left Content */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-block px-3 py-1 mb-4 border border-indigo-500 rounded-full bg-indigo-900/30 text-indigo-400 text-sm font-semibold tracking-wide">
            NEXT LEVEL CODING
          </div>

          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-6 text-gray-100 font-aligran">
            Advanced Python
            <span className="text-indigo-500 block">Django & Data Science</span>
          </h1>

          <p className="text-xl text-gray-300 max-w-2xl mb-8">
            Go beyond the basics. Master <strong>Web Development</strong>, <strong>REST APIs</strong>, and <strong>Data Analytics</strong> to become a complete Python Developer.
          </p>

          <button
            onClick={() =>
              document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth" })
            }
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 px-10 rounded-lg shadow-[0_5px_20px_rgba(79,70,229,0.3)] transition duration-300 transform hover:scale-105"
          >
            Start Learning
          </button>
        </motion.div>

        {/* Right Side Image (Python Advanced Logo) */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9 }}
          className="flex justify-center md:justify-end"
        >
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/c/c3/Python-logo-notext.svg" 
            alt="Python Advanced"
            className="w-64 md:w-72 
                       drop-shadow-[0_5px_30px_rgba(99,102,241,0.4)]
                       transition-transform duration-300 
                       hover:scale-105"
          />
        </motion.div>
      </div>
      </section>

      {/* --- WHY LEARN SECTION --- */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6 text-center">

          <h2 className="text-4xl md:text-5xl font-extrabold mb-4 text-gray-900 font-aligran">
            Why <span className="text-indigo-600">Advanced Python?</span>
          </h2>

          <p className="text-gray-600 mb-14 max-w-3xl mx-auto text-lg leading-relaxed">
            Core Python is just the alphabet. Advanced Python allows you to write the stories—Web Apps, APIs, and Data Models.
          </p>

          <div className="grid md:grid-cols-3 gap-8">

            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-14 h-14 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition">
                <Globe size={28} />
              </div>
              <h3 className="font-bold text-2xl text-black mb-3">Full Stack Power</h3>
              <p className="text-gray-600 leading-relaxed">
                With Django & Flask, you can build powerful backends for websites and mobile apps.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-14 h-14 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition">
                <Brain size={28} />
              </div>
              <h3 className="font-bold text-2xl text-black mb-3">Data Science Entry</h3>
              <p className="text-gray-600 leading-relaxed">
                Learn NumPy and Pandas, the stepping stones to a career in AI and Machine Learning.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-14 h-14 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition">
                <BarChart size={28} />
              </div>
              <h3 className="font-bold text-2xl text-black mb-3">High Salaries</h3>
              <p className="text-gray-600 leading-relaxed">
                Python Full Stack and Data Engineers are among the highest paid professionals globally.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* --- HIGHLIGHTS SECTION --- */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900 font-aligran">
            Course <span className="text-indigo-600">Highlights</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {highlights.map((item, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition overflow-hidden flex h-44 border-l-4 border-indigo-600"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="w-1/2 p-5 flex flex-col justify-center">
                  <h3 className="font-bold text-lg text-black mb-1">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
                <div className="w-1/2 relative">
                  <img src={item.img} alt={item.title} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-l from-black/40 to-transparent"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- CURRICULUM SECTION --- */}
      <section className="py-20 bg-white"> 
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-left text-gray-900 font-aligran"> 
            Detailed <span className="text-indigo-600">Curriculum</span>
          </h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50"> 
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-100 hover:bg-gray-200 transition flex justify-between items-center text-gray-900"> 
                    {module.title}
                    <span className="transition-transform group-open:rotate-180 text-gray-900">▼</span>
                  </summary>
                  <div className="px-6 py-4 text-black border-t border-gray-200 leading-relaxed"> 
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- TOOLS SECTION --- */}
      <ToolsYouWillMaster />

      {/* --- FAQ & FORM SECTION --- */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-10 bg-white"> 
        <div className="md:col-span-2">
          <h2 className="text-3xl font-bold mb-8 text-gray-900 font-aligran">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqContent.map((item, idx) => (
              <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg"> 
                <details className="group p-4">
                  <summary className="cursor-pointer font-medium text-gray-900 hover:text-gray-700 flex justify-between">
                    {item.title}
                    <span className="text-gray-900 group-open:text-indigo-600">+</span>
                  </summary>
                  <p className="mt-4 text-black text-sm border-t border-gray-200 pt-3">{item.answer}</p> 
                </details>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-1" id="enquiry-form">
          <div className="bg-gray-100 border border-indigo-300 rounded-xl p-6 shadow-xl sticky top-10">
            <h3 className="text-2xl font-bold mb-2 text-center text-indigo-600">Book Your Seat</h3>
            <p className="text-center text-gray-600 mb-6 text-sm">Level up your Python skills.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="text" name="firstName" placeholder="Your Name" required className="w-full bg-white border border-gray-300 rounded p-3 text-black focus:border-indigo-500 focus:outline-none" value={formData.firstName} onChange={handleInputChange} />
              <input type="email" name="businessEmail" placeholder="Email Address" required className="w-full bg-white border border-gray-300 rounded p-3 text-black focus:border-indigo-500 focus:outline-none" value={formData.businessEmail} onChange={handleInputChange} />
              <input type="tel" name="phone" placeholder="Phone Number" required className="w-full bg-white border border-gray-300 rounded p-3 text-black focus:border-indigo-500 focus:outline-none" value={formData.phone} onChange={handleInputChange} />
              <button type="submit" className="w-full bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-bold py-3 rounded transition shadow-lg">ENQUIRE NOW</button>
              <p className="text-xs text-center text-gray-500 mt-2">We respect your privacy.</p>
            </form>
          </div>
        </div>
      </section>

      {/* --- TESTIMONIALS SECTION --- */}
      <section className="bg-gray-50 py-20 border-t border-gray-200"> 
        <div className="max-w-7xl mx-auto px-6 text-left"> 
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900 font-aligran">
                Success <span className="text-indigo-600">Stories</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
                <AnimatePresence mode="popLayout">
                    {currentVisibleTestimonials.map((testimonial) => (
                        <motion.div
                            key={testimonial.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.4 }}
                            className="bg-white p-6 rounded-xl border border-gray-200 flex flex-col text-left hover:border-indigo-400/50 transition shadow-sm"
                        >
                            <div className="flex items-center mb-4">
                                <img src={testimonial.profileImg} alt={testimonial.name} className="w-12 h-12 rounded-full border-2 border-indigo-500 mr-3" />
                                <div>
                                    <h4 className="font-bold text-black">{testimonial.name}</h4> 
                                    <span className="text-gray-500 text-xs">{testimonial.source}</span>
                                </div>
                            </div>
                            <div className="mb-3"><StarRating rating={testimonial.rating} /></div>
                            <p className="text-gray-700 text-sm flex-grow">"{testimonial.review}"</p> 
                            <VerifiedBadge />
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
            <div className="flex justify-start mt-8 space-x-2"> 
                {testimonials.map((_, idx) => (
                    <button
                        key={idx}
                        onClick={() => setCurrentTestimonialIndex(idx)}
                        className={`h-2 w-2 rounded-full transition-all ${idx === currentTestimonialIndex ? 'bg-indigo-600 w-6' : 'bg-gray-300'}`}
                    />
                ))}
            </div>
        </div>
      </section>
    </div>
  );
};

export default AdvancePython;